import { fetchStock, assertStock, assertCartStock, stockMessage, deliveryProperties, syncDelivery } from './colet-stock.js?v=air-sea-20261027-2';

class ColetProduct extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-colet-pdp-form]');
    this.variantInput = this.querySelector('[data-colet-pdp-variant-id]');
    this.sizeInputs = [...this.querySelectorAll('[data-colet-pdp-size-input]')];
    this.sizeLabel = this.querySelector('[data-colet-pdp-size-label]');
    this.stockNote = this.querySelector('[data-colet-pdp-stock]');
    this.stockLimits = JSON.parse(this.querySelector('[data-colet-pdp-stock-limits]')?.textContent || '{}');
    this.delivery = JSON.parse(this.querySelector('[data-colet-pdp-delivery]')?.textContent || '{}');
    this.cartQuantities = {};
    this.price = this.querySelector('[data-colet-pdp-price]');
    this.sku = this.querySelector('[data-colet-pdp-sku]');
    this.addButton = this.querySelector('[data-colet-pdp-add]');
    this.checkoutButton = this.querySelector('[data-colet-pdp-checkout]');
    this.notifyTrigger = this.querySelector('[data-colet-pdp-notify-trigger]');
    this.notifyDialog = this.querySelector('[data-colet-notify-dialog]');
    this.notifyForm = this.querySelector('[data-colet-notify-form]');
    this.notifySizeInputs = [...this.querySelectorAll('[data-colet-notify-size]')];
    this.completedNotifyRequests = new Set();
    this.completedMarketingEmails = new Set();
    this.notifyInFlight = false;
    this.notifyPanel = this.querySelector('[data-colet-pdp-notify]');
    this.notifyEmail = this.querySelector('[data-colet-pdp-notify-email]');
    this.notifySubmit = this.querySelector('[data-colet-pdp-notify-submit]');
    this.notifyMarketing = this.querySelector('[data-colet-pdp-notify-marketing]');
    this.notifyMessage = this.querySelector('[data-colet-pdp-notify-message]');
    this.notifyTitle = this.querySelector('[data-colet-pdp-notify-title]');
    this.notifySuccessCopy = this.querySelector('[data-colet-pdp-notify-success-copy]');
    this.message = this.querySelector('[data-colet-pdp-message]');
    this.gallery = this.querySelector('[data-colet-pdp-gallery]');
    this.sticky = this.querySelector('.colet-pdp__sticky');
    this.dots = [...this.querySelectorAll('.colet-pdp__dot')];
    this.details = [...this.querySelectorAll('.colet-pdp__detail')];
    this.detailSummaries = this.details.map((detail) => detail.querySelector('summary')).filter(Boolean);
    this.detailAnimations = new WeakMap();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.variants = this.readVariants();
    this.updateSizeStockState();
    this.currentVariant = null;
    this.fallbackSku = this.sku?.textContent || '';
    this.fallbackPrice = this.price?.textContent || '';
    this.isDemoMode = this.dataset.demoMode === 'true';
    this.isPreview = this.dataset.launchState === 'preview';
    this.notifyResetTimer = null;

    this.handleSizeChange = this.handleSizeChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCheckout = this.handleCheckout.bind(this);
    this.handleNotifySubmit = this.handleNotifySubmit.bind(this);
    this.handleNotifyInput = this.handleNotifyInput.bind(this);
    this.handleNotifyKeydown = this.handleNotifyKeydown.bind(this);
    this.handleGalleryScroll = this.handleGalleryScroll.bind(this);
    this.handleDetailSummaryClick = this.handleDetailSummaryClick.bind(this);
    this.handleStickyWheel = this.handleStickyWheel.bind(this);

    this.sizeInputs.forEach((input) => input.addEventListener('change', this.handleSizeChange));
    this.form?.addEventListener('submit', this.handleSubmit);
    this.checkoutButton?.addEventListener('click', this.handleCheckout);
    this.notifyForm?.addEventListener('submit', this.handleNotifySubmit);
    this.setupNotifyDrawer();
    this.notifyEmail?.addEventListener('input', this.handleNotifyInput);
    this.notifyEmail?.addEventListener('keydown', this.handleNotifyKeydown);
    this.gallery?.addEventListener('scroll', this.handleGalleryScroll, { passive: true });
    this.detailSummaries.forEach((summary) => summary.addEventListener('click', this.handleDetailSummaryClick));
    this.sticky?.addEventListener('wheel', this.handleStickyWheel, { passive: false });

    this.handleDeliveryRefresh = () => this.refreshDelivery();
    document.addEventListener('cart:update', this.handleDeliveryRefresh);
    window.addEventListener('pageshow', this.handleDeliveryRefresh);
    this.refreshDelivery();
    this.selectVariantFromUrl();
    this.updateState();
    this.handleGalleryScroll();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.handleDeliveryRefresh);
    window.removeEventListener('pageshow', this.handleDeliveryRefresh);
    this.sizeInputs.forEach((input) => input.removeEventListener('change', this.handleSizeChange));
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.checkoutButton?.removeEventListener('click', this.handleCheckout);
    this.notifyForm?.removeEventListener('submit', this.handleNotifySubmit);
    this.notifyController?.abort();
    clearTimeout(this.notifyCloseTimer);
    cancelAnimationFrame(this.notifyOpenFrame);
    if (this.notifyDialog?.open) {
      this.notifyDialog.close();
      document.documentElement.classList.remove('colet-notify-open');
    }
    this.notifyEmail?.removeEventListener('input', this.handleNotifyInput);
    this.notifyEmail?.removeEventListener('keydown', this.handleNotifyKeydown);
    this.gallery?.removeEventListener('scroll', this.handleGalleryScroll);
    this.detailSummaries.forEach((summary) => summary.removeEventListener('click', this.handleDetailSummaryClick));
    this.sticky?.removeEventListener('wheel', this.handleStickyWheel);
    window.clearTimeout(this.notifyResetTimer);
  }

  async refreshDelivery() {
    try {
      const stock = await fetchStock();
      if (!this.isConnected) return;
      this.applyStock(stock);
      this.updateState();
    } catch { }
  }

  applyStock(stock) {
    this.stockLimits = stock.limits;
    this.delivery = stock.delivery || this.delivery;
    this.cartQuantities = {};
    stock.items.forEach((item) => { this.cartQuantities[item.variantId] = (this.cartQuantities[item.variantId] || 0) + item.quantity; });
    this.updateSizeStockState();
  }

  updateDeliveryLabel() {
    const data = this.delivery[this.currentVariant?.id];
    const ready = data ? Math.max(0, data.ready - (this.cartQuantities[this.currentVariant?.id] || 0)) : null;
    if (this.addButton) this.addButton.textContent = data && ready === 0 ? data.preorderLabel : this.dataset.addLabel;
    const property = this.form?.querySelector('input[name^="properties["]');
    if (property) property.disabled = !data || ready > 0;
  }

  readVariants() {
    const script = this.querySelector('[data-colet-pdp-variants]');
    if (!script?.textContent) return [];

    try {
      return JSON.parse(script.textContent);
    } catch {
      return [];
    }
  }

  selectVariantFromUrl() {
    const variantId = Number(new URLSearchParams(window.location.search).get('variant'));
    if (this.isPreview || !variantId || !this.sizeInputs.length) return;

    const variant = this.variants.find((item) => Number(item.id) === variantId);
    if (!variant) return;

    this.currentVariant = variant;
    const input = this.sizeInputs.find((sizeInput) => {
      const optionIndex = Number(sizeInput.dataset.optionPosition || 1) - 1;
      return variant.options?.[optionIndex] === sizeInput.value;
    });

    if (input) {
      input.checked = true;
    }
  }

  selectedSizeInput() {
    return this.sizeInputs.find((input) => input.checked);
  }

  handleSizeChange() {
    this.updateState();
  }

  updateState() {
    if (this.isPreview) {
      this.currentVariant = null;
      this.variantInput.value = '';
      this.setPurchaseState('preview');
      this.writeFallbackMeta();
      return;
    }

    if (!this.sizeInputs.length) {
      this.currentVariant = this.variants.find((variant) => this.isVariantAvailable(variant)) || this.variants[0] || null;
      this.writeVariant();
      return;
    }

    const selectedInput = this.selectedSizeInput();

    if (!selectedInput) {
      this.currentVariant = null;
      this.variantInput.value = '';
      this.sizeLabel.textContent = this.dataset.selectSizeLabel || 'Size';
      this.setPurchaseState(this.hasAnyAvailableSize() ? 'unselected' : 'soldout');
      this.writeFallbackMeta();
      return;
    }

    const optionIndex = Number(selectedInput.dataset.optionPosition || 1) - 1;
    this.currentVariant =
      this.variants.find((variant) => variant.options?.[optionIndex] === selectedInput.value && this.isVariantAvailable(variant)) ||
      this.variants.find((variant) => variant.options?.[optionIndex] === selectedInput.value) ||
      null;

    this.sizeLabel.textContent = this.selectedSizeLabel(selectedInput.value);
    this.clearMessage();
    this.writeVariant();
  }

  writeVariant() {
    this.updateStockNote();
    if (!this.currentVariant) {
      this.setPurchaseState('soldout');
      return;
    }

    this.variantInput.value = this.currentVariant.id;
    this.setPurchaseState(this.isVariantAvailable(this.currentVariant) ? 'available' : 'soldout');

    if (this.price && Number.isFinite(this.currentVariant.price)) {
      this.price.textContent = this.formatMoney(this.currentVariant.price);
    }

    if (this.sku) {
      this.sku.textContent = this.fallbackSku;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', this.currentVariant.id);
    window.history.replaceState({}, '', url.toString());
  }

  isVariantAvailable(variant) {
    return Boolean(variant?.available) && this.stockLimits[variant.id] !== 0;
  }

  updateSizeStockState() {
    this.sizeInputs.forEach((input) => {
      const variant = this.variants.find((entry) => String(entry.id) === input.dataset.variantId);
      const available = this.isVariantAvailable(variant);
      input.dataset.variantAvailable = String(available);
      input.nextElementSibling?.classList.toggle('is-unavailable', !available);
    });
  }

  updateStockNote() {
    if (!this.stockNote) return;
    const quantity = this.stockLimits[this.currentVariant?.id];
    const show = !this.isPreview && this.isVariantAvailable(this.currentVariant) && Number.isFinite(quantity) && quantity > 0 && quantity < 3;
    this.stockNote.hidden = !show;
    this.stockNote.textContent = show ? (quantity === 1 ? this.dataset.oneStockMessage : this.dataset.lowStockMessage) : '';
  }

  setPurchaseState(state) {
    if (!this.addButton || !this.checkoutButton || !this.notifyPanel) return;

    const isSoldOut = this.isPreview || state === 'soldout';
    if (this.isPreview) state = 'preview';
    this.addButton.disabled = isSoldOut;
    this.checkoutButton.disabled = isSoldOut;

    this.addButton.hidden = isSoldOut;
    this.checkoutButton.hidden = isSoldOut;
    if (this.notifyTrigger) {
      this.notifyTrigger.hidden = !isSoldOut;
      this.notifyTrigger.textContent = this.isPreview ? this.dataset.notifyPreviewCta : this.dataset.notifyButton;
    }

    if (!isSoldOut) {
      this.resetNotifyState(false);
    } else {
      this.setNotifyMode(this.isPreview ? 'launch' : 'size');
    }

    this.updateDeliveryLabel();
    this.dataset.purchaseState = state;
  }

  hasAnyAvailableSize() {
    if (!this.sizeInputs.length) return this.variants.some((variant) => this.isVariantAvailable(variant));

    return this.sizeInputs.some((input) => input.dataset.variantAvailable === 'true');
  }

  writeFallbackMeta() {
    this.updateStockNote();
    if (this.price) this.price.textContent = this.fallbackPrice;
    if (this.sku) this.sku.textContent = this.fallbackSku;
  }

  selectedSizeLabel(value) {
    const template = this.dataset.selectedSizeTemplate || 'Size: [[size]]';

    return template.replace('[[size]]', value);
  }

  formatMoney(cents) {
    const amount = Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
        maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
      }).format(amount);
    } catch {
      return `$${amount.toFixed(amount % 1 === 0 ? 0 : 2)}`;
    }
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (!this.ensureVariantSelected()) return;

    await this.addToCart(false);
  }

  async handleCheckout() {
    if (!this.ensureVariantSelected()) return;

    await this.addToCart(true);
  }

  setupNotifyDrawer() {
    if (!this.notifyDialog || !this.notifyTrigger) return;
    this.notifyController = new AbortController();
    const options = { signal: this.notifyController.signal };
    this.notifyTrigger.addEventListener('click', () => this.openNotifyDrawer(), options);
    this.notifyDialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-colet-notify-close]')) this.closeNotifyDrawer();
      else if (event.target === this.notifyDialog) {
        const r = this.notifyDialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.closeNotifyDrawer();
      }
    }, options);
    this.notifyDialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closeNotifyDrawer();
    }, options);
    this.notifyDialog.addEventListener('close', () => {
      this.notifyDialog.removeAttribute('data-visible');
      document.documentElement.classList.remove('colet-notify-open');
      if (this.notifyTrigger.isConnected) this.notifyTrigger.focus({ preventScroll: true });
    }, options);
    this.notifyDialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const controls = [...this.notifyDialog.querySelectorAll('button, [href], input, select, [tabindex]')]
        .filter((control) => !control.disabled && control.tabIndex >= 0 && control.getClientRects().length);
      if (!controls.length) return;
      const index = controls.indexOf(document.activeElement);
      if (event.shiftKey && index <= 0) {
        event.preventDefault(); controls.at(-1).focus();
      } else if (!event.shiftKey && (index === controls.length - 1 || index === -1)) {
        event.preventDefault(); controls[0].focus();
      }
    }, options);
    this.notifySizeInputs.forEach((input) => input.addEventListener('change', () => {
      if (this.notifyInFlight) return;
      this.resetNotifyState(false);
    }, options));
  }

  selectedNotifyVariant() {
    if (this.isPreview) return null;
    const id = this.notifySizeInputs.find((input) => input.checked)?.value;
    return this.variants.find((variant) => String(variant.id) === id) || null;
  }

  openNotifyDrawer() {
    clearTimeout(this.notifyCloseTimer);
    if (!this.notifyInFlight) {
      this.resetNotifyState(false);
      this.notifySizeInputs.forEach((input) => {
        input.checked = !this.isVariantAvailable(this.currentVariant) && String(this.currentVariant?.id) === input.value;
      });
    }
    document.querySelector('[data-colet-cart-drawer][open]')?.close?.();
    document.querySelector('colet-size-guide-drawer:has(dialog[open])')?.close?.();
    if (!this.notifyDialog.open) this.notifyDialog.showModal();
    document.documentElement.classList.add('colet-notify-open');
    this.notifyOpenFrame = requestAnimationFrame(() => {
      this.notifyDialog.setAttribute('data-visible', '');
      if (!this.notifyInFlight) {
        const focusTarget = this.isPreview || this.selectedNotifyVariant() ? this.notifyEmail : this.notifySizeInputs[0];
        focusTarget?.focus({ preventScroll: true });
      }
    });
  }

  closeNotifyDrawer() {
    cancelAnimationFrame(this.notifyOpenFrame);
    this.notifyDialog.removeAttribute('data-visible');
    clearTimeout(this.notifyCloseTimer);
    this.notifyCloseTimer = setTimeout(() => this.notifyDialog.close(), this.reducedMotion.matches ? 0 : 220);
  }

  handleNotifyInput() {
    if (this.notifyInFlight) return;
    this.notifyEmail?.removeAttribute('aria-invalid');
    this.notifyPanel?.classList.toggle('has-email', Boolean(this.notifyEmail?.value.trim()));

    if (this.notifyPanel?.dataset.notifyState === 'warning' || this.notifyPanel?.dataset.notifyState === 'joined') {
      window.clearTimeout(this.notifyResetTimer);
      this.setNotifyState('start');
    }
  }

  handleNotifyKeydown(event) {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    this.handleNotifySubmit(event);
  }

  async handleNotifySubmit(event) {
    event?.preventDefault?.();
    if (!this.notifyEmail || !this.notifySubmit || this.notifyInFlight) return;
    const email = this.notifyEmail.value.trim().toLowerCase();
    this.notifyEmail.value = email;
    if (!this.isValidEmail(email) || !this.notifyEmail.checkValidity()) {
      this.setNotifyState('warning', email ? this.dataset.notifyInvalidEmail : this.dataset.notifyEmptyEmail);
      this.notifyEmail.setAttribute('aria-invalid', 'true');
      this.notifyEmail.focus();
      return;
    }
    this.notifyEmail.removeAttribute('aria-invalid');
    const variant = this.selectedNotifyVariant();
    if (!this.isPreview && (!variant || this.isVariantAvailable(variant))) {
      this.setNotifyState('warning', this.dataset.notifySelectSize);
      this.notifySizeInputs[0]?.focus();
      return;
    }
    const requestKey = [email, this.isPreview ? 'launch' : 'restock', this.dataset.productId, this.isPreview ? '' : variant.id].join('|');
    const wantsMarketing = Boolean(this.notifyMarketing?.checked);
    if (this.completedNotifyRequests.has(requestKey) && (!wantsMarketing || this.completedMarketingEmails.has(email))) {
      this.setNotifyState('warning', this.dataset.notifyDuplicate);
      return;
    }
    this.notifyInFlight = true;
    this.setNotifyState('joining');
    window.clearTimeout(this.notifyResetTimer);
    try {
      if (wantsMarketing && !this.dataset.klaviyoNewsletterListId) throw new Error(this.dataset.notifyConfigError);
      if (!this.completedNotifyRequests.has(requestKey)) {
        await this.createKlaviyoIntent(email, variant);
        this.completedNotifyRequests.add(requestKey);
      }
      if (wantsMarketing && !this.completedMarketingEmails.has(email)) {
        await this.submitMarketingConsent(email);
        this.completedMarketingEmails.add(email);
      }
      this.setNotifyState('joined');
      this.querySelector('[data-colet-pdp-notify-success]')?.focus();
      this.notifyResetTimer = window.setTimeout(() => {
        const refocusEmail = this.notifyDialog.open && document.activeElement === this.querySelector('[data-colet-pdp-notify-success]');
        this.resetNotifyState(true);
        if (refocusEmail) this.notifyEmail.focus({ preventScroll: true });
      }, 4000);
    } catch (error) {
      this.setNotifyState('warning', this.dataset.notifyError);
    } finally {
      this.notifyInFlight = false;
    }
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  setNotifyMode(mode) {
    if (!this.notifyPanel) return;

    const isSizeWaitlist = mode === 'size';
    this.notifyPanel.dataset.notifyMode = mode;

    if (this.notifyTitle) {
      this.notifyTitle.textContent = this.dataset.notifyTitle;
    }

    if (this.notifySuccessCopy) {
      this.notifySuccessCopy.textContent = isSizeWaitlist
        ? this.dataset.notifySavedSize
        : this.dataset.notifySavedLaunch;
    }
  }

  async createKlaviyoIntent(email, variant = this.selectedNotifyVariant()) {
    const companyId = this.dataset.klaviyoCompanyId;
    if (!companyId || (this.isPreview && !this.dataset.klaviyoPreviewListId)) throw new Error(this.dataset.notifyConfigError);
    const profile = {data: {type: 'profile', attributes: {email}}};
    let endpoint;
    let payload;
    if (this.isPreview) {
      endpoint = 'events';
      payload = {data: {type: 'event', attributes: {
        metric: {data: {type: 'metric', attributes: {name: 'Collection 001 Preview Requested'}}},
        profile,
        unique_id: 'colet-collection001-launch-' + this.dataset.productId,
        properties: {
          ProductID: this.dataset.productId,
          ProductName: this.dataset.productTitle,
          ProductHandle: this.dataset.productHandle,
          ProductURL: window.location.origin + '/products/' + this.dataset.productHandle,
          Color: this.dataset.productColor,
          VariantID: null,
          Size: 'Any size',
          SKU: '',
          Collection: 'Collection 001',
          NotificationType: 'Pre-order availability',
          Source: 'Product preview waitlist'
        }
      }}};
    } else {
      if (!variant || this.isVariantAvailable(variant)) throw new Error(this.dataset.notifySelectSize);
      endpoint = 'back-in-stock-subscriptions';
      payload = {data: {type: 'back-in-stock-subscription',
        attributes: {profile, channels: ['EMAIL']},
        relationships: {variant: {data: {type: 'catalog-variant', id: '$shopify:::$default:::' + variant.id}}}
      }};
    }
    const response = await fetch('https://a.klaviyo.com/client/' + endpoint + '/?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: {'Content-Type': 'application/vnd.api+json', accept: 'application/vnd.api+json', revision: '2026-07-15'},
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(20000)
    });
    if (!response.ok) throw new Error(this.dataset.notifyError);
    if (this.isPreview) {
      const subscription = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: {'Content-Type': 'application/vnd.api+json', accept: 'application/vnd.api+json', revision: '2026-07-15'},
        body: JSON.stringify({data: {type: 'subscription',
          attributes: {
            custom_source: 'Collection 001 availability notification request',
            profile: {data: {type: 'profile', attributes: {email,
              subscriptions: {email: {marketing: {consent: 'SUBSCRIBED'}}}
            }}}
          },
          relationships: {list: {data: {type: 'list', id: this.dataset.klaviyoPreviewListId}}}
        }}),
        signal: AbortSignal.timeout(20000)
      });
      if (!subscription.ok) throw new Error(this.dataset.notifyError);
    }
  }

  async submitMarketingConsent(email) {
    const companyId = this.dataset.klaviyoCompanyId;
    const listId = this.dataset.klaviyoNewsletterListId;
    if (!companyId || !listId) throw new Error(this.dataset.notifyConfigError);

    const response = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: {'Content-Type': 'application/vnd.api+json', accept: 'application/vnd.api+json', revision: '2026-07-15'},
      body: JSON.stringify({data: {type: 'subscription',
        attributes: {
          custom_source: 'Product Notify Me newsletter opt-in',
          profile: {data: {type: 'profile', attributes: {email,
            subscriptions: {email: {marketing: {consent: 'SUBSCRIBED'}}}
          }}}
        },
        relationships: {list: {data: {type: 'list', id: listId}}}
      }}),
      signal: AbortSignal.timeout(20000)
    });
    if (!response.ok) throw new Error(this.dataset.notifyError);
  }

  setNotifyState(state, message = '') {
    if (!this.notifyPanel || !this.notifySubmit) return;

    this.notifyPanel.dataset.notifyState = state;
    this.notifyPanel.setAttribute('aria-busy', String(state === 'joining'));
    if (this.notifyEmail) this.notifyEmail.disabled = state === 'joining';
    if (this.notifyMarketing) this.notifyMarketing.disabled = state === 'joining';
    this.notifySizeInputs.forEach((input) => { input.disabled = state === 'joining'; });
    if (state !== 'warning') this.notifyEmail?.removeAttribute('aria-invalid');
    this.notifySubmit.setAttribute('aria-label', state === 'joining' ? this.dataset.notifyLoading : this.dataset.notifySubmitLabel);
    this.notifySubmit.disabled = state === 'joining' || state === 'joined';

    const label = this.dataset.notifySubmitLabel;

    if (state === 'joining') {
      this.notifySubmit.textContent = '•••';
    } else {
      this.notifySubmit.textContent = label;
    }

    if (this.notifyMessage) {
      const shouldShowMessage = Boolean(message) && state === 'warning';
      this.notifyMessage.textContent = message;
      this.notifyMessage.hidden = !shouldShowMessage;
    }
  }

  resetNotifyState(clearEmail = true) {
    window.clearTimeout(this.notifyResetTimer);

    if (clearEmail && this.notifyEmail) {
      this.notifyEmail.value = '';
      if (this.notifyMarketing) this.notifyMarketing.checked = this.notifyMarketing.defaultChecked;
    }

    this.notifyPanel?.classList.toggle('has-email', Boolean(this.notifyEmail?.value.trim()));
    this.setNotifyState('start');
  }

  handleDetailSummaryClick(event) {
    const activeDetail = event.currentTarget.closest('.colet-pdp__detail');
    if (!activeDetail) return;

    event.preventDefault();

    if (activeDetail.open) {
      this.closeDetail(activeDetail);
      return;
    }

    this.details.forEach((detail) => {
      if (detail !== activeDetail && detail.open) {
        this.closeDetail(detail);
      }
    });

    this.openDetail(activeDetail);
  }

  openDetail(detail) {
    const content = detail.querySelector('.colet-pdp__detail-content');
    if (!content) {
      detail.open = true;
      return;
    }

    this.cancelDetailAnimation(detail);
    detail.classList.remove('is-closing');
    detail.open = true;

    if (!content.animate || this.reducedMotion.matches) {
      this.resetDetailContent(content);
      this.keepDetailAboveFooter(content);
      return;
    }

    const targetHeight = content.scrollHeight;
    content.style.overflow = 'hidden';
    content.style.height = '0px';
    content.style.opacity = '0';
    content.getBoundingClientRect();

    const animation = content.animate(
      [
        { height: '0px', opacity: 0 },
        { height: `${targetHeight}px`, opacity: 1 },
      ],
      {
        duration: 240,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'both',
      }
    );

    this.detailAnimations.set(detail, animation);
    animation.addEventListener(
      'finish',
      () => {
        this.detailAnimations.delete(detail);
        this.resetDetailContent(content);
        this.keepDetailAboveFooter(content);
      },
      { once: true }
    );
  }

  closeDetail(detail) {
    const content = detail.querySelector('.colet-pdp__detail-content');
    if (!content) {
      detail.open = false;
      return;
    }

    this.cancelDetailAnimation(detail);
    detail.classList.add('is-closing');

    if (!content.animate || this.reducedMotion.matches) {
      detail.open = false;
      detail.classList.remove('is-closing');
      this.resetDetailContent(content);
      return;
    }

    const startHeight = content.scrollHeight;
    content.style.overflow = 'hidden';
    content.style.height = `${startHeight}px`;
    content.style.opacity = '1';
    content.getBoundingClientRect();

    const animation = content.animate(
      [
        { height: `${startHeight}px`, opacity: 1 },
        { height: '0px', opacity: 0 },
      ],
      {
        duration: 200,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        fill: 'both',
      }
    );

    this.detailAnimations.set(detail, animation);
    animation.addEventListener(
      'finish',
      () => {
        detail.open = false;
        detail.classList.remove('is-closing');
        this.detailAnimations.delete(detail);
        this.resetDetailContent(content);
      },
      { once: true }
    );
  }

  cancelDetailAnimation(detail) {
    const animation = this.detailAnimations.get(detail);
    if (!animation) return;

    animation.cancel();
    this.detailAnimations.delete(detail);
  }

  resetDetailContent(content) {
    content.style.height = '';
    content.style.opacity = '';
    content.style.overflow = '';
  }

  keepDetailAboveFooter(content) {
    window.requestAnimationFrame(() => {
      const footer = document.querySelector('.colet-footer');
      if (!footer || !content) return;

      const minimumGap = 1;
      const guardElement = this.querySelector('.colet-pdp__panel-content') || content;
      const contentRect = guardElement.getBoundingClientRect();
      const footerRect = footer.getBoundingClientRect();
      const overlap = contentRect.bottom + minimumGap - footerRect.top;
      if (overlap <= 0) return;

      if (this.sticky) {
        const stickyRoom = this.sticky.scrollHeight - this.sticky.clientHeight - this.sticky.scrollTop;
        const stickyShift = Math.min(overlap, Math.max(stickyRoom, 0));

        if (stickyShift > 0) {
          this.sticky.scrollBy({ top: stickyShift, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
          window.setTimeout(() => this.keepDetailAboveFooter(content), this.reducedMotion.matches ? 0 : 260);
          return;
        }
      }

      const updatedOverlap = guardElement.getBoundingClientRect().bottom + minimumGap - footer.getBoundingClientRect().top;
      if (updatedOverlap > 0) {
        window.scrollBy({ top: -updatedOverlap, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
      }
    });
  }

  handleStickyWheel(event) {
    if (!this.sticky || this.sticky.scrollHeight <= this.sticky.clientHeight + 1) return;

    const isScrollingDown = event.deltaY > 0;
    const isAtTop = this.sticky.scrollTop <= 0;
    const isAtBottom = Math.ceil(this.sticky.scrollTop + this.sticky.clientHeight) >= this.sticky.scrollHeight;

    if ((isScrollingDown && isAtBottom) || (!isScrollingDown && isAtTop)) {
      event.preventDefault();
      window.scrollBy({
        top: event.deltaY,
        left: event.deltaX,
        behavior: 'auto',
      });
    }
  }

  ensureVariantSelected() {
    if (this.currentVariant?.id) return true;

    this.sizeLabel?.classList.add('is-error');
    this.showMessage(this.dataset.selectSizeMessage || 'Please select a size.');
    this.querySelector('[data-colet-pdp-size]')?.scrollIntoView({ block: 'center', behavior: 'smooth' });

    window.setTimeout(() => {
      this.sizeLabel?.classList.remove('is-error');
    }, 900);

    return false;
  }

  async addToCart(goToCheckout) {
    if (this.isPreview || !this.isVariantAvailable(this.currentVariant) || this.hasAttribute('aria-busy')) return;

    if (this.isDemoMode) {
      this.showMessage(this.dataset.demoMessage || 'This demo product is not connected to inventory yet.');
      return;
    }

    this.setBusy(true);
    this.clearMessage();

    try {
      const previousDelivery = this.delivery[this.currentVariant.id];
      const expectedReady = previousDelivery && previousDelivery.ready > (this.cartQuantities[this.currentVariant.id] || 0);
      const stock = await fetchStock();
      this.applyStock(stock);
      this.writeVariant();
      assertStock(stock, this.currentVariant.id, 1);
      const freshDelivery = this.delivery[this.currentVariant.id];
      if (expectedReady && freshDelivery && freshDelivery.ready <= (this.cartQuantities[this.currentVariant.id] || 0)) {
        throw new Error(this.dataset.deliverySelectionChangedMessage);
      }
      const sectionId = this.cartDrawerSectionId();
      let properties = {};
      for (const [name, value] of new FormData(this.form)) {
        if (name.startsWith('properties[') && name.endsWith(']')) {
          properties[name.slice(11, -1)] = value;
        }
      }
      properties = deliveryProperties(stock, this.currentVariant.id, 1, properties);
      const body = {
        items: [{ id: Number(this.currentVariant.id), quantity: 1, properties }],
      };

      if (sectionId) {
        body.sections = sectionId;
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const cartResponse = await response.json();

      if (!response.ok) {
        throw new Error(cartResponse.description || 'Unable to add item to cart');
      }

      const cartResource = await this.ensureCartResource(cartResponse);

      if (goToCheckout) {
        const checkoutStock = await fetchStock();
        assertCartStock(checkoutStock);
        const delivery = await syncDelivery(checkoutStock);
        if (delivery.needsReview) throw new Error(this.dataset.deliveryChangedMessage);
        assertCartStock(await fetchStock());
        window.location.href = this.dataset.checkoutUrl || '/checkout';
        return;
      }

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cartResource,
            data: {
              itemCount: cartResource.item_count,
              sections: cartResource.sections,
              source: 'colet-product',
            },
          },
        })
      );

      document.querySelector('[data-colet-cart-drawer]')?.open?.();
    } catch (error) {
      this.showMessage(error.message?.startsWith('stock-') ? stockMessage(this, error) : (error.message || this.dataset.addErrorMessage));
    } finally {
      this.setBusy(false);
    }
  }

  async ensureCartResource(cartResponse) {
    if (Number.isFinite(cartResponse?.item_count)) return cartResponse;

    const response = await fetch(this.dataset.cartUrl || '/cart.js', {
      headers: { Accept: 'application/json' },
    });
    const cart = await response.json();

    if (cartResponse?.sections) {
      cart.sections = cartResponse.sections;
    }

    return cart;
  }

  cartDrawerSectionId() {
    return document.querySelector('[data-colet-cart-drawer]')?.dataset.sectionId || '';
  }

  setBusy(isBusy) {
    this.toggleAttribute('aria-busy', isBusy);
    [this.addButton, this.checkoutButton].forEach((button) => {
      if (button) button.disabled = isBusy || this.dataset.purchaseState === 'soldout' || this.isPreview;
    });
  }

  showMessage(message) {
    if (!this.message) return;

    this.message.textContent = message;
    this.message.hidden = false;
  }

  clearMessage() {
    if (!this.message) return;

    this.message.textContent = '';
    this.message.hidden = true;
  }

  handleGalleryScroll() {
    if (!this.gallery || !this.dots.length) return;

    const index = Math.round(this.gallery.scrollLeft / Math.max(this.gallery.clientWidth, 1));
    this.dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
  }
}

if (!customElements.get('colet-product')) {
  customElements.define('colet-product', ColetProduct);
}

